"use strict";
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlangEditor = void 0;
var react_1 = __importDefault(require("react"));
var react_codemirror_1 = __importDefault(require("@uiw/react-codemirror"));
// codemirror-lang-BLANG is our custom package for BLANG language support
// https://gitlab.besedo.com/moderation-platform/codemirror-lang-blang
var codemirror_lang_BLANG_1 = require("codemirror-lang-BLANG");
var codemirror_themes_1 = __importDefault(require("@uiw/codemirror-themes"));
var highlight_1 = require("@lezer/highlight");
var view_1 = require("@codemirror/view");
var autocomplete_1 = require("@codemirror/autocomplete");
var clsx_1 = __importDefault(require("clsx"));
var data_1 = require("./data");
// Custom completion function for BLANG operators
function operatorCompletions(context) {
    // First check if we're starting with a $ character, which indicates a variable
    var dollarMatch = context.matchBefore(/\$\w*$/);
    if (dollarMatch)
        return null;
    // Now look for words that might need operator completion
    // eslint-disable-next-line no-useless-escape
    var word = context.matchBefore(/[\w\+\-\*\/\=\!\>\<\&\|]+/);
    if (!word)
        return null;
    return {
        from: word.from,
        options: data_1.blangOperators,
        filter: true,
    };
}
// Custom completion function for BLANG variables
function variableCompletions(context) {
    // Match input that starts with a dollar sign (both just $ and $followed.by.more)
    var dollarRegex = /\$([\w.]*)?$/;
    var match = context.matchBefore(dollarRegex);
    // Only return suggestions when we have a match that starts with $
    if (!match)
        return null;
    // Check if the input starts with double dollar signs ($$), if so, don't show suggestions
    var doubleMatch = context.matchBefore(/\$\$/);
    if (doubleMatch)
        return null;
    return {
        from: match.from,
        options: data_1.blangVariables,
        // Don't filter when just $ is typed, otherwise filter based on what's typed
        filter: match.text !== '$',
    };
}
// Function to determine the current operator context
function getOperatorContext(context) {
    // Look for operators like MATCH, SIMILAR, or other operators before @
    var beforeText = context.state.sliceDoc(0, context.pos);
    // Check for MATCH operator
    if (/MATCH\s+@?\s*$/.test(beforeText)) {
        return 'MATCH';
    }
    // Check for SIMILAR operator
    if (/SIMILAR\s+@?\s*$/.test(beforeText)) {
        return 'SIMILAR';
    }
    // No specific operator context found
    return null;
}
// Custom completion function for dictionaries with @ prefix
function dictionaryCompletions(context, dictionaries) {
    var atWordRegex = /@([\w-]*)$/;
    var match = context.matchBefore(atWordRegex);
    if (!match || !(dictionaries === null || dictionaries === void 0 ? void 0 : dictionaries.length))
        return null;
    // Get the current operator context
    var operatorContext = getOperatorContext(context);
    // Filter dictionaries based on operator context
    var filteredDictionaries = __spreadArray([], __read(dictionaries), false);
    if (operatorContext === 'MATCH') {
        // Only CONFUSABLE dictionaries for MATCH operator
        filteredDictionaries = dictionaries.filter(function (dict) { return dict.kind === 'CONFUSABLE'; });
    }
    else if (operatorContext === 'SIMILAR') {
        // Only PHASH dictionaries for SIMILAR operator
        filteredDictionaries = dictionaries.filter(function (dict) { return dict.kind === 'PHASH'; });
    }
    else {
        // Default to MIXED dictionaries when no specific operator context
        filteredDictionaries = dictionaries.filter(function (dict) { return dict.kind === 'MIXED'; });
    }
    return {
        from: match.from,
        options: filteredDictionaries.map(function (dict) { return ({
            label: "@".concat(dict.name),
            type: dict.kind,
            // detail: `Dictionary: ${dict.description || dict.name}`,
            detail: "(".concat(dict.kind, ")"),
            apply: "@".concat(dict.name),
        }); }),
        filter: true,
    };
}
// Function to create theme with or without syntax highlighting
var createBlangTheme = function (disableHighlight) {
    if (disableHighlight === void 0) { disableHighlight = false; }
    return (0, codemirror_themes_1.default)({
        theme: 'light',
        settings: {
            background: '#ffffff',
            foreground: 'black',
            caret: '#5d00ff',
            selection: '#036dd626',
            selectionMatch: '#036dd626',
            lineHighlight: '#ffffff1a',
            gutterBackground: '#fff',
            gutterForeground: '#8a919966',
            fontFamily: 'Helvetica Neue, Arial, sans-serif',
        },
        styles: disableHighlight
            ? [] // Empty styles array disables syntax highlighting
            : [
                { tag: highlight_1.tags.string, class: 'cm-string' },
                { tag: highlight_1.tags.operator, class: 'cm-operator' },
                { tag: highlight_1.tags.variableName, class: 'cm-variable' },
                { tag: highlight_1.tags.bool, class: 'cm-boolean' },
                { tag: highlight_1.tags.list, class: 'cm-list' },
                { tag: highlight_1.tags.number, class: 'cm-number' },
                { tag: highlight_1.tags.regexp, class: 'cm-regex' },
                { tag: highlight_1.tags.lineComment, class: 'cm-comment' },
                { tag: highlight_1.tags.special(highlight_1.tags.variableName), class: 'cm-customer-specific-variable' },
                { tag: highlight_1.tags.controlKeyword, class: 'cm-logical-operators' },
            ],
    });
};
function BlangEditor(_a) {
    var editable = _a.editable, value = _a.value, onChange = _a.onChange, height = _a.height, width = _a.width, maxHeight = _a.maxHeight, hasError = _a.hasError, dictionaries = _a.dictionaries, _b = _a.enableAutocomplete, enableAutocomplete = _b === void 0 ? false : _b, _c = _a.resizable, resizable = _c === void 0 ? false : _c, errorMsg = _a.errorMsg, _d = _a.disableHighlight, disableHighlight = _d === void 0 ? false : _d;
    // Create a function that uses the dictionaries prop
    var dictionaryCompletionWithData = function (context) { return dictionaryCompletions(context, dictionaries); };
    // Create extensions array with conditional autocompletion
    var extensions = [(0, codemirror_lang_BLANG_1.BLANG)(), view_1.EditorView.lineWrapping];
    // Only add autocompletion if enableAutocomplete is true
    if (enableAutocomplete) {
        extensions.push((0, autocomplete_1.autocompletion)({
            override: [variableCompletions, operatorCompletions, dictionaryCompletionWithData],
            icons: true,
        }));
    }
    // Create theme based on disableHighlight prop
    var blangTheme = createBlangTheme(disableHighlight);
    return (react_1.default.createElement("div", { className: (0, clsx_1.default)('editor-container', { 'has-error': !!errorMsg }) },
        react_1.default.createElement("div", { className: (0, clsx_1.default)('editor-wrapper', {
                'resizable-editor': resizable,
            }), style: {
                height: resizable ? height : 'auto',
                width: resizable ? width : 'auto',
                maxHeight: maxHeight,
                resize: resizable ? 'both' : 'none',
                overflow: resizable ? 'hidden' : 'auto',
            } },
            react_1.default.createElement(react_codemirror_1.default, { className: (0, clsx_1.default)('react-code-mirror-blang-mode', {
                    hasError: hasError,
                }), theme: blangTheme, value: value, onChange: onChange, extensions: extensions, readOnly: !editable, editable: editable, height: resizable ? '100%' : height, width: resizable ? '100%' : width })),
        errorMsg && react_1.default.createElement("p", { className: "invalid-blang-warning" }, errorMsg)));
}
exports.BlangEditor = BlangEditor;
